import React, { useState, useEffect } from 'react';
import './index.css';
import { MessageCircle, Play, ChevronLeft, ChevronRight, ExternalLink, Cloud, Star, DollarSign, Users, Rocket, ArrowRight, Heart, Zap, Settings } from 'lucide-react';

// ============================================
// CONFIGURATION
// ============================================
const CONFIG = {
  showAcquisitionSection: true,
  showRoadmapSection: true,
  footerStyle: 'embed',
  
  socials: {
    discord: 'https://discord.gg/GbzFfJ7vkM',
    twitter: 'https://twitter.com/CloudriseRB',
    youtube: 'https://youtube.com/@CloudriseRB',
  },
  
  navLinks: [
    { label: 'Home', href: '#home' },
    { label: 'About', href: '#about' },
    { label: 'Games', href: '#games' },
  ],
  
  // Games - use PLACE IDs (the ID in the game URL)
  games: [
    // { placeId: '920587237', name: 'Adopt Me', robloxUrl: 'https://www.roblox.com/games/920587237' },
    // { placeId: '130594398886540', name: 'Garden Horizons', robloxUrl: 'https://www.roblox.com/games/130594398886540' },
    // { placeId: '14314519903', name: 'Come Hang', robloxUrl: 'https://www.roblox.com/games/14314519903' },
    // { placeId: '132351861405107', name: 'Critter Bay', robloxUrl: 'https://www.roblox.com/games/132351861405107' },
  ],
};

const ROBLOX_ENDPOINTS = {
  universeByPlace: 'https://apis.roproxy.com/universes/v1/places',
  games: 'https://games.roproxy.com/v1/games',
  gameIcons: 'https://thumbnails.roproxy.com/v1/games/icons',
};

// ============================================
// BRAND COLORS
// ============================================
const COLORS = {
  heading: 'rgb(43, 43, 43)',
  text: 'rgba(43, 43, 43, 0.95)',
  pink: '#E84C8A',
  purple: '#9B6EBF',
  deepPurple: '#3D2D5C',
  cyan: '#5CDDE8',
  yellow: '#F9E154',
  orange: '#F5B342',
  lime: '#C5E17A',
  mint: '#7EDCD2',
  white: '#FFFFFF',
};

// ============================================
// HEADER
// ============================================
const Header = ({ scrolled, isSecondaryPage = false }) => (
  <header
    className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
    style={{
      background: scrolled ? 'rgba(255, 255, 255, 0.72)' : 'rgba(255, 255, 255, 0.62)',
      backdropFilter: scrolled ? 'blur(28px) saturate(160%)' : 'blur(24px) saturate(150%)',
      WebkitBackdropFilter: scrolled ? 'blur(28px) saturate(160%)' : 'blur(24px) saturate(150%)',
      borderBottom: '1px solid rgba(255, 255, 255, 0.45)',
      boxShadow: scrolled ? '0 8px 30px rgba(0,0,0,0.08)' : '0 4px 20px rgba(0,0,0,0.04)',
    }}
  >
    <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
      <a href={isSecondaryPage ? '/' : '#home'} className="flex items-center gap-3">
        <img src="/logo.png" alt="Cloudrise logo" className="w-12 h-12 object-contain" />
        <span className="text-2xl font-black" style={{ fontFamily: 'Outfit, sans-serif', color: COLORS.heading }}>
          Cloudrise
        </span>
      </a>

      <div className="hidden md:block w-px h-8 bg-gray-200 mx-6" />

      <nav className="hidden md:flex items-center gap-8">
        {CONFIG.navLinks.map((link) => (
          <a
            key={link.label}
            href={isSecondaryPage ? `/${link.href}` : link.href}
            className="font-medium transition-colors hover:opacity-70"
            style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(43,43,43,0.6)' }}
          >
            {link.label}
          </a>
        ))}
      </nav>
    </div>
  </header>
);

// ============================================
// HERO SECTION
// ============================================
const HeroSection = () => {
  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden" style={{ background: COLORS.white }}>
      
      {/* Rainbow stripes accent */}
      <div className="absolute -right-10 top-1/2 -translate-y-1/2 flex gap-3 rotate-12">
        <div className="w-5 h-80 rounded-full" style={{ background: COLORS.pink, opacity: 0.25 }} />
        <div className="w-5 h-80 rounded-full" style={{ background: COLORS.yellow, opacity: 0.25 }} />
        <div className="w-5 h-80 rounded-full" style={{ background: COLORS.cyan, opacity: 0.25 }} />
      </div>
      <div className="absolute -left-10 top-1/3 flex gap-3 -rotate-12">
        <div className="w-4 h-60 rounded-full" style={{ background: COLORS.purple, opacity: 0.2 }} />
        <div className="w-4 h-60 rounded-full" style={{ background: COLORS.pink, opacity: 0.2 }} />
        <div className="w-4 h-60 rounded-full" style={{ background: COLORS.orange, opacity: 0.2 }} />
      </div>
      
      {/* 6 DECORATIONS */}
      <div className="absolute pointer-events-none" style={{ top: '18%', left: '8%', animation: 'floatGentle 7s ease-in-out infinite' }}>
        <svg width={70} height={42} viewBox="0 0 100 60" fill={COLORS.pink}>
          <ellipse cx="25" cy="38" rx="22" ry="18" />
          <ellipse cx="50" cy="30" rx="28" ry="24" />
          <ellipse cx="75" cy="38" rx="22" ry="18" />
          <ellipse cx="50" cy="42" rx="40" ry="18" />
        </svg>
      </div>
      
      <div className="absolute pointer-events-none" style={{ top: '25%', left: '22%', animation: 'twinkle 4s ease-in-out infinite', animationDelay: '0.5s' }}>
        <svg width={35} height={35} viewBox="0 0 24 24" fill={COLORS.orange}>
          <path d="M12 2L14.09 8.26L20.18 9.27L15.54 13.14L16.82 19.02L12 16.27L7.18 19.02L8.46 13.14L3.82 9.27L9.91 8.26L12 2Z" />
        </svg>
      </div>
      
      <div className="absolute pointer-events-none" style={{ top: '20%', right: '10%' }}>
        <svg width={55} height={55} viewBox="0 0 24 24" fill={COLORS.cyan} style={{ animation: 'spin 25s linear infinite' }}>
          <path d="M12 15.5A3.5 3.5 0 0 1 8.5 12 3.5 3.5 0 0 1 12 8.5a3.5 3.5 0 0 1 3.5 3.5 3.5 3.5 0 0 1-3.5 3.5m7.43-2.53c.04-.32.07-.64.07-.97 0-.33-.03-.66-.07-1l2.11-1.63c.19-.15.24-.42.12-.64l-2-3.46c-.12-.22-.39-.31-.61-.22l-2.49 1c-.52-.39-1.06-.73-1.69-.98l-.37-2.65A.506.506 0 0 0 14 2h-4c-.25 0-.46.18-.5.42l-.37 2.65c-.63.25-1.17.59-1.69.98l-2.49-1c-.22-.09-.49 0-.61.22l-2 3.46c-.13.22-.07.49.12.64L4.57 11c-.04.34-.07.67-.07 1 0 .33.03.65.07.97l-2.11 1.66c-.19.15-.25.42-.12.64l2 3.46c.12.22.39.3.61.22l2.49-1.01c.52.4 1.06.74 1.69.99l.37 2.65c.04.24.25.42.5.42h4c.25 0 .46-.18.5-.42l.37-2.65c.63-.26 1.17-.59 1.69-.99l2.49 1.01c.22.08.49 0 .61-.22l2-3.46c.12-.22.07-.49-.12-.64l-2.11-1.66z"/>
        </svg>
      </div>
      
      <div className="absolute pointer-events-none" style={{ bottom: '28%', left: '12%' }}>
        <svg width={45} height={45} viewBox="0 0 24 24" fill={COLORS.purple} style={{ animation: 'spin 20s linear infinite reverse' }}>
          <path d="M12 15.5A3.5 3.5 0 0 1 8.5 12 3.5 3.5 0 0 1 12 8.5a3.5 3.5 0 0 1 3.5 3.5 3.5 3.5 0 0 1-3.5 3.5m7.43-2.53c.04-.32.07-.64.07-.97 0-.33-.03-.66-.07-1l2.11-1.63c.19-.15.24-.42.12-.64l-2-3.46c-.12-.22-.39-.31-.61-.22l-2.49 1c-.52-.39-1.06-.73-1.69-.98l-.37-2.65A.506.506 0 0 0 14 2h-4c-.25 0-.46.18-.5.42l-.37 2.65c-.63.25-1.17.59-1.69.98l-2.49-1c-.22-.09-.49 0-.61.22l-2 3.46c-.13.22-.07.49.12.64L4.57 11c-.04.34-.07.67-.07 1 0 .33.03.65.07.97l-2.11 1.66c-.19.15-.25.42-.12.64l2 3.46c.12.22.39.3.61.22l2.49-1.01c.52.4 1.06.74 1.69.99l.37 2.65c.04.24.25.42.5.42h4c.25 0 .46-.18.5-.42l.37-2.65c.63-.26 1.17-.59 1.69-.99l2.49 1.01c.22.08.49 0 .61-.22l2-3.46c.12-.22.07-.49-.12-.64l-2.11-1.66z"/>
        </svg>
      </div>
      
      <div className="absolute pointer-events-none" style={{ bottom: '25%', right: '20%', animation: 'twinkle 3.5s ease-in-out infinite', animationDelay: '1s' }}>
        <svg width={30} height={30} viewBox="0 0 24 24" fill={COLORS.yellow}>
          <path d="M12 2L14.09 8.26L20.18 9.27L15.54 13.14L16.82 19.02L12 16.27L7.18 19.02L8.46 13.14L3.82 9.27L9.91 8.26L12 2Z" />
        </svg>
      </div>
      
      <div className="absolute pointer-events-none" style={{ bottom: '32%', right: '6%', animation: 'floatGentle 8s ease-in-out infinite', animationDelay: '0.5s' }}>
        <svg width={60} height={36} viewBox="0 0 100 60" fill={COLORS.mint} opacity={0.8}>
          <ellipse cx="25" cy="38" rx="22" ry="18" />
          <ellipse cx="50" cy="30" rx="28" ry="24" />
          <ellipse cx="75" cy="38" rx="22" ry="18" />
          <ellipse cx="50" cy="42" rx="40" ry="18" />
        </svg>
      </div>
      
      {/* Hero Content */}
      <div className="relative z-10 text-center px-6 max-w-5xl mx-auto pt-24">
        
        <h1 className="text-5xl md:text-7xl lg:text-8xl font-black mb-8 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <span style={{ color: COLORS.heading }}>Where </span>
          <span style={{ color: COLORS.pink }}>imagination</span>
          <br />
          <span style={{ color: COLORS.heading }}>takes </span>
          <span style={{ color: COLORS.cyan }}>flight</span>
          <span style={{ color: COLORS.heading }}>.</span>
        </h1>
        
        <p className="text-xl md:text-2xl mb-12 max-w-2xl mx-auto" style={{ fontFamily: 'Outfit, sans-serif', color: COLORS.text }}>
          We create immersive, impactful experiences on Roblox.
        </p>
        
        {/* Showreel */}
        <div className="relative mt-8 mb-8 max-w-3xl mx-auto">
          <div className="aspect-video rounded-3xl overflow-hidden shadow-2xl group cursor-pointer relative" style={{ background: COLORS.deepPurple }}>
            <div className="absolute top-4 left-4">
              <Star size={24} fill={COLORS.orange} color={COLORS.orange} />
            </div>
            <div className="absolute top-4 right-4">
              <Star size={20} fill={COLORS.yellow} color={COLORS.yellow} />
            </div>
            <div className="absolute bottom-4 left-4">
              <Cloud size={30} color={COLORS.white} style={{ opacity: 0.3 }} />
            </div>
            <div className="absolute bottom-4 right-20">
              <Settings size={22} color={COLORS.white} style={{ opacity: 0.2 }} />
            </div>
            
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-20 h-20 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg" style={{ background: COLORS.white }}>
                <Play size={40} className="ml-1" fill={COLORS.pink} color={COLORS.pink} />
              </div>
            </div>
            <div className="absolute bottom-4 right-4 text-sm font-medium" style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255,255,255,0.8)' }}>
              Watch our showreel
            </div>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
          <div className="w-6 h-10 border-2 rounded-full flex justify-center pt-2" style={{ borderColor: COLORS.pink }}>
            <div className="w-1.5 h-3 rounded-full animate-bounce" style={{ background: COLORS.pink }} />
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// MISSION SECTION
// ============================================
const MissionSection = () => (
  <section id="about" className="py-32 relative overflow-hidden" style={{ background: COLORS.white }}>
    <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
      <h2 className="text-4xl md:text-6xl font-black mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
        <span style={{ color: COLORS.heading }}>Creating </span>
        <span style={{ color: COLORS.pink }}>immersive</span>
        <span style={{ color: COLORS.heading }}> experiences,</span>
        <br />
        <span style={{ color: COLORS.cyan }}>one step</span>
        <span style={{ color: COLORS.heading }}> at a time.</span>
      </h2>
      
      <p className="text-xl md:text-2xl leading-relaxed max-w-3xl mx-auto" style={{ fontFamily: 'Outfit, sans-serif', color: COLORS.text }}>
        We blend creativity, passion, and innovation to build unforgettable 
        experiences on Roblox. Every experience we create is a new world to explore.
      </p>

      </div>
  </section>
);

// ============================================
// GAMES SECTION - Live Roblox API
// ============================================
const GameCard = ({ game }) => {
  const [gameName, setGameName] = useState(game.name);
  const [gameUrl, setGameUrl] = useState(game.robloxUrl);
  const [playerCount, setPlayerCount] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    let intervalId = null;

    const fetchThumbnail = async (universeId) => {
      if (!universeId) return;
      try {
        const response = await fetch(
          `${ROBLOX_ENDPOINTS.gameIcons}?universeIds=${universeId}&size=512x512&format=Png&isCircular=false`
        );
        const data = await response.json();
        if (mounted && data.data && data.data[0]) {
          setThumbnail(data.data[0].imageUrl);
        }
      } catch (error) {
        console.error('Failed to fetch thumbnail:', error);
      }
    };

    const fetchUniverseId = async () => {
      try {
        const response = await fetch(
          `${ROBLOX_ENDPOINTS.universeByPlace}/${game.placeId}/universe`
        );
        const data = await response.json();
        if (data.universeId) {
          return data.universeId;
        }
      } catch (error) {
        console.error('Failed to fetch universe ID:', error);
      }
      return null;
    };

    const fetchPlayerCount = async (universeId) => {
      if (!universeId) return;
      try {
        const response = await fetch(
          `${ROBLOX_ENDPOINTS.games}?universeIds=${universeId}`
        );
        const data = await response.json();
        if (mounted && data.data && data.data[0]) {
          const gameData = data.data[0];
          setGameName(gameData.name || game.name);
          setPlayerCount(gameData.playing ?? null);
          if (gameData.rootPlaceId) {
            setGameUrl(`https://www.roblox.com/games/${gameData.rootPlaceId}`);
          }
        }
      } catch (error) {
        console.error('Failed to fetch player count:', error);
      }
    };

    const initialize = async () => {
      const universeId = await fetchUniverseId();
      await Promise.all([
        fetchThumbnail(universeId),
        fetchPlayerCount(universeId),
      ]);
      if (mounted) setLoading(false);

      intervalId = setInterval(() => {
        fetchPlayerCount(universeId);
      }, 30000);
    };

    initialize();

    return () => {
      mounted = false;
      if (intervalId) clearInterval(intervalId);
    };
  }, [game.placeId]);

  return (
    <a 
      href={gameUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="block flex-shrink-0 w-72 transition-transform hover:scale-105 hover:-translate-y-2"
    >
      <div className="cute-card rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all" style={{ background: COLORS.white }}>
        {/* Thumbnail */}
        <div className="aspect-video relative overflow-hidden" style={{ background: `${COLORS.purple}20` }}>
          {thumbnail ? (
            <img 
              src={thumbnail} 
              alt={game.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <Cloud size={50} color={COLORS.purple} style={{ opacity: 0.3 }} />
            </div>
          )}
        </div>
        
        {/* Game info */}
        <div className="p-4">
          <h3 className="text-lg font-bold mb-2 truncate" style={{ fontFamily: 'Outfit, sans-serif', color: COLORS.heading }}>
            {gameName}
          </h3>
          
          {/* Live player count */}
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#22c55e' }} />
            <span className="text-sm font-medium" style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(43,43,43,0.6)' }}>
              {loading ? '...' : (playerCount !== null ? playerCount.toLocaleString() : '—')} playing
            </span>
          </div>
        </div>
      </div>
    </a>
  );
};

const GamesSection = () => {
  const scrollContainerRef = React.useRef(null);
  
  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300;
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section id="games" className="py-32 relative overflow-hidden" style={{ background: COLORS.white }}>
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-black mb-4" style={{ fontFamily: 'Outfit, sans-serif', color: COLORS.heading }}>
            Our <span style={{ color: COLORS.pink }}>Games</span>
          </h2>
          <p className="text-xl" style={{ fontFamily: 'Outfit, sans-serif', color: COLORS.text }}>
            Check out our vast range of immersive experiences
          </p>
        </div>
        
        <div className="relative">
          <button 
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-12 h-12 rounded-full shadow-lg flex items-center justify-center hover:scale-110 transition-transform"
            style={{ background: COLORS.white }}
          >
            <ChevronLeft size={24} style={{ color: COLORS.heading }} />
          </button>
          
          <div 
            ref={scrollContainerRef}
            className="flex gap-6 overflow-x-auto pb-4 px-2 scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {CONFIG.games.map((game) => (
              <GameCard key={game.placeId} game={game} />
            ))}
          </div>
          
          <button 
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-12 h-12 rounded-full shadow-lg flex items-center justify-center hover:scale-110 transition-transform"
            style={{ background: COLORS.white }}
          >
            <ChevronRight size={24} style={{ color: COLORS.heading }} />
          </button>
        </div>
        
        <div className="text-center mt-10">
          <a 
            href="https://www.roblox.com/groups/32972796"
            target="_blank"
            rel="noopener noreferrer"
            className="cute-cta inline-flex items-center gap-3 text-white px-8 py-4 rounded-full font-bold text-lg shadow-xl hover:scale-105 transition-all"
            style={{ fontFamily: 'Outfit, sans-serif', background: COLORS.pink }}
          >
            <ExternalLink size={20} />
            See All
          </a>
        </div>
      </div>
      
      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
      `}</style>
    </section>
  );
};

// ============================================
// ACQUISITION SECTION
// ============================================
const AcquisitionSection = () => {
  if (!CONFIG.showAcquisitionSection) return null;
  
  const features = [
    { icon: Heart, title: 'We Buy Your Game', description: 'Got a game you\'re proud of? We\'ll give it a loving new home with a fair upfront offer.', color: COLORS.pink },
    { icon: Rocket, title: 'We Make It Soar', description: 'Our team brings fresh updates, marketing magic, and the polish your game deserves.', color: COLORS.cyan },
    { icon: DollarSign, title: 'You Keep Earning', description: 'Stay connected to your creation with ongoing revenue share. Your game keeps giving back.', color: COLORS.orange },
    { icon: Zap, title: 'Zero Hassle Handoff', description: 'We handle the transition smoothly so you can start dreaming up your next big idea.', color: COLORS.purple },
  ];

  return (
    <section className="py-32 relative overflow-hidden" style={{ background: COLORS.white }}>
      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-black mb-4" style={{ fontFamily: 'Outfit, sans-serif', color: COLORS.heading }}>
            Let's Team Up
          </h2>
          <p className="text-xl max-w-2xl mx-auto" style={{ fontFamily: 'Outfit, sans-serif', color: COLORS.text }}>
            Built something amazing? We'd love to help it reach the clouds. Here's how we can collaborate.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-6">
          {features.map((feature, index) => (
            <div key={index} className="cute-card rounded-3xl p-8 transition-all hover:scale-[1.02] shadow-lg" style={{ background: COLORS.white }}>
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6" style={{ background: feature.color }}>
                <feature.icon size={28} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif', color: COLORS.heading }}>
                {feature.title}
              </h3>
              <p style={{ fontFamily: 'Outfit, sans-serif', color: COLORS.text }}>
                {feature.description}
              </p>
            </div>
          ))}
        </div>
        
      </div>
    </section>
  );
};

// ============================================
// ROADMAP SECTION
// ============================================
const RoadmapSection = () => {
  if (!CONFIG.showRoadmapSection) return null;

  return (
    <section className="py-32 relative overflow-hidden" style={{ background: COLORS.white }}>
      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-black mb-4" style={{ fontFamily: 'Outfit, sans-serif', color: COLORS.heading }}>
            How We <span style={{ color: COLORS.cyan }}>Build</span>
          </h2>
          <p className="text-xl" style={{ fontFamily: 'Outfit, sans-serif', color: COLORS.text }}>
            Our approach to creating magical experiences
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-8">
          <div className="cute-card rounded-3xl p-10 shadow-xl hover:shadow-2xl transition-shadow" style={{ background: COLORS.white }}>
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6" style={{ background: COLORS.pink }}>
              <Heart size={32} className="text-white" />
            </div>
            <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif', color: COLORS.heading }}>
              Creative First
            </h3>
            <p className="text-lg leading-relaxed" style={{ fontFamily: 'Outfit, sans-serif', color: COLORS.text }}>
              We start every project with imagination at the core. Before a single line of code, 
              we design immersive worlds that players will love to explore.
            </p>
          </div>
          
          <div className="cute-card rounded-3xl p-10 shadow-xl hover:shadow-2xl transition-shadow" style={{ background: COLORS.white }}>
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6" style={{ background: COLORS.cyan }}>
              <Users size={32} className="text-white" />
            </div>
            <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif', color: COLORS.heading }}>
              Player-Driven
            </h3>
            <p className="text-lg leading-relaxed" style={{ fontFamily: 'Outfit, sans-serif', color: COLORS.text }}>
              Our community shapes what we build. We listen to feedback and continuously iterate 
              to make experiences more fun and engaging.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// SOCIAL ICONS
// ============================================
const SocialIcon = ({ type, href, size = 24, className = '' }) => {
  const icons = {
    discord: <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/></svg>,
    twitter: <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>,
    youtube: <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>,
  };
  return <a href={href} target="_blank" rel="noopener noreferrer" className={`hover:scale-110 transition-transform ${className}`}>{icons[type]}</a>;
};

// ============================================
// STAY IN TOUCH
// ============================================
const StayInTouchPill = () => {
  if (CONFIG.footerStyle !== 'pill') return null;
  
  return (
    <section className="py-24 relative overflow-hidden" style={{ background: '#faf9ff' }}>
      <div className="max-w-4xl mx-auto px-6 relative z-10">
        <div className="rounded-[3rem] p-12 text-center relative overflow-hidden shadow-2xl" style={{ background: COLORS.pink }}>
          <h2 className="text-3xl md:text-4xl font-black text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Stay in touch
          </h2>
          <p className="mb-8 text-lg" style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255,255,255,0.85)' }}>
            Get the latest updates and join our community
          </p>
          <div className="flex justify-center gap-6">
            <SocialIcon type="discord" href={CONFIG.socials.discord} size={32} className="text-white hover:text-yellow-300" />
            <SocialIcon type="twitter" href={CONFIG.socials.twitter} size={32} className="text-white hover:text-yellow-300" />
            <SocialIcon type="youtube" href={CONFIG.socials.youtube} size={32} className="text-white hover:text-yellow-300" />
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// TWITTER EMBED
// ============================================
const TwitterEmbedSection = () => {
  const timelineRef = React.useRef(null);

  useEffect(() => {
    let cancelled = false;

    const ensureTwitterScript = () =>
      new Promise((resolve, reject) => {
        if (window.twttr?.widgets) {
          resolve(window.twttr);
          return;
        }

        const existing = document.querySelector('script[src="https://platform.twitter.com/widgets.js"]');
        if (existing) {
          existing.addEventListener('load', () => resolve(window.twttr), { once: true });
          existing.addEventListener('error', reject, { once: true });
          return;
        }

        const script = document.createElement('script');
        script.src = 'https://platform.twitter.com/widgets.js';
        script.async = true;
        script.onload = () => resolve(window.twttr);
        script.onerror = reject;
        document.body.appendChild(script);
      });

    const renderTimeline = async () => {
      const container = timelineRef.current;
      if (!container) return;

      // Let Twitter mutate only this isolated subtree.
      container.innerHTML = '';
      const anchor = document.createElement('a');
      anchor.className = 'twitter-timeline';
      anchor.setAttribute('data-height', '512');
      anchor.setAttribute('data-width', '100%');
      anchor.setAttribute('data-theme', 'light');
      anchor.href = 'https://twitter.com/CloudriseRB';
      anchor.textContent = 'Tweets by CloudriseRB';
      container.appendChild(anchor);

      try {
        const twttr = await ensureTwitterScript();
        if (cancelled) return;
        twttr?.widgets?.load(container);
      } catch (error) {
        console.error('Failed to load Twitter timeline:', error);
      }
    };

    renderTimeline();

    return () => {
      cancelled = true;
      if (timelineRef.current) {
        timelineRef.current.innerHTML = '';
      }
    };
  }, []);

  if (CONFIG.footerStyle !== 'embed') return null;

  return (
    <section className="py-24 relative overflow-hidden" style={{ background: COLORS.white }}>
      <div className="max-w-4xl mx-auto px-6 relative z-10">
        <div className="text-center mb-12">
          <h2
            className="text-4xl md:text-5xl font-black mb-4"
            style={{ fontFamily: 'Outfit, sans-serif', color: COLORS.heading }}
          >
            What we're <span style={{ color: COLORS.pink }}>up to</span>
          </h2>
        </div>
        <div ref={timelineRef} className="max-w-xl mx-auto" />
      </div>
    </section>
  );
};

const TermsSection = () => (
  <section className="py-28 relative overflow-hidden" style={{ background: COLORS.white }}>
    <div className="max-w-4xl mx-auto px-6">
      <h1 className="text-4xl md:text-5xl font-black mb-6" style={{ fontFamily: 'Outfit, sans-serif', color: COLORS.heading }}>
        Terms of Service
      </h1>
      <p className="mb-8" style={{ fontFamily: 'Outfit, sans-serif', color: COLORS.text }}>
        Last updated: March 8, 2026
      </p>
      <div className="space-y-6" style={{ fontFamily: 'Outfit, sans-serif', color: COLORS.text }}>
        <p>By using Cloudrise services, websites, or Roblox experiences, you agree to these terms.</p>
        <p>You agree not to misuse our services, interfere with security, or violate applicable laws and platform rules.</p>
        <p>All content, branding, and materials are owned by Cloudrise or licensed to us, unless otherwise stated.</p>
        <p>Services are provided as-is and as-available. Cloudrise is not liable for indirect or consequential damages.</p>
        <p>We may update these terms at any time. Continued use after updates means you accept the revised terms.</p>
      </div>
    </div>
  </section>
);

const PrivacySection = () => (
  <section className="py-28 relative overflow-hidden" style={{ background: COLORS.white }}>
    <div className="max-w-4xl mx-auto px-6">
      <h1 className="text-4xl md:text-5xl font-black mb-6" style={{ fontFamily: 'Outfit, sans-serif', color: COLORS.heading }}>
        Privacy Policy
      </h1>
      <p className="mb-8" style={{ fontFamily: 'Outfit, sans-serif', color: COLORS.text }}>
        Last updated: March 8, 2026
      </p>
      <div className="space-y-6" style={{ fontFamily: 'Outfit, sans-serif', color: COLORS.text }}>
        <p>We collect basic account and usage information needed to provide and improve our services.</p>
        <p>If you sign in with Roblox, we process identity information returned by Roblox for authentication.</p>
        <p>We do not sell personal information. We may share data with service providers that help operate the site.</p>
        <p>You can request access, correction, or deletion of personal data by contacting us.</p>
        <p>We may update this policy periodically, and updated versions will be posted on this page.</p>
      </div>
    </div>
  </section>
);

// ============================================
// FOOTER
// ============================================
const Footer = () => (
  <footer className="py-12" style={{ background: COLORS.deepPurple }}>
    <div className="max-w-6xl mx-auto px-6">
      {CONFIG.footerStyle === 'pill' ? (
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <img src="/logo.png" alt="Cloudrise logo" className="w-10 h-10 object-contain" />
            <div>
              <div className="font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>CLOUDRISE</div>
              <div style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255,255,255,0.6)', fontSize: 14 }}>Copyright © 2026 Cloudrise.</div>
            </div>
          </div>
          <div style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255,255,255,0.6)', fontSize: 14 }}>All rights reserved.</div>
          <nav className="flex gap-6">
            {CONFIG.navLinks.map((link) => (
              <a key={link.label} href={link.href} className="transition-colors hover:text-white" style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255,255,255,0.6)' }}>
                {link.label}
              </a>
            ))}
          </nav>
        </div>
      ) : (
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255,255,255,0.6)', fontSize: 14 }}>Copyright © 2026 Cloudrise. All rights reserved.</div>
          <div className="flex gap-4">
            <SocialIcon type="discord" href={CONFIG.socials.discord} className="text-white hover:text-white" />
            <SocialIcon type="twitter" href={CONFIG.socials.twitter} className="text-white hover:text-white" />
            <SocialIcon type="youtube" href={CONFIG.socials.youtube} className="text-white hover:text-white" />
          </div>
        </div>
      )}
    </div>
  </footer>
);

// ============================================
// MAIN APP
// ============================================
export default function CloudriseWebsite() {
  const [scrolled, setScrolled] = useState(false);
  const isTermsPage = window.location.pathname === '/terms';
  const isPrivacyPage = window.location.pathname === '/privacy';
  const isSecondaryPage = isTermsPage || isPrivacyPage;
  
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleAnchorClick = (event) => {
      const target = event.target.closest('a[href^="#"]');
      if (!target) return;

      const id = target.getAttribute('href');
      if (!id || id === '#') return;

      const element = document.querySelector(id);
      if (!element) return;

      event.preventDefault();
      const headerOffset = 96;
      const elementTop = element.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: elementTop - headerOffset,
        behavior: 'smooth',
      });
    };

    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);

  return (
    <div className="site-root" style={{ fontFamily: 'Outfit, sans-serif', scrollBehavior: 'smooth' }}>
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&display=swap" rel="stylesheet" />
      <style>{`
        .site-root section[id] {
          scroll-margin-top: 96px;
        }

        .site-root .cute-card {
          transition: transform 280ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 280ms ease;
        }

        .site-root .cute-card:hover {
          transform: translateY(-4px);
        }

        .site-root .cute-cta {
          transition: transform 220ms cubic-bezier(0.22, 1, 0.36, 1), filter 220ms ease;
        }

        .site-root .cute-cta:hover {
          transform: translateY(-2px) scale(1.02);
          filter: saturate(1.08);
        }

        @media (prefers-reduced-motion: reduce) {
          .site-root html,
          .site-root * {
            scroll-behavior: auto !important;
          }

          .site-root .cute-card,
          .site-root .cute-card:hover,
          .site-root .cute-cta,
          .site-root .cute-cta:hover {
            transform: none;
            transition: none;
          }
        }
      `}</style>
      
      <Header scrolled={scrolled} isSecondaryPage={isSecondaryPage} />
      {isTermsPage ? (
        <>
          <div className="pt-20" />
          <TermsSection />
        </>
      ) : isPrivacyPage ? (
        <>
          <div className="pt-20" />
          <PrivacySection />
        </>
      ) : (
        <>
          <HeroSection />
          <MissionSection />
          <GamesSection />
          <AcquisitionSection />
          <RoadmapSection />
          <StayInTouchPill />
          <TwitterEmbedSection />
        </>
      )}
      <Footer />
    </div>
  );
}
